import { useMemo } from "react";
import { AutocompleteOption, SelectInputProps } from "../../components/SortFilter/interfaces";
import { OptionTypeVO } from "../../types/model/valueObject/OptionTypeVO";

type OptionType = AutocompleteOption | SelectInputProps;
type VariableInputOptionType = string[] | OptionTypeVO[] | OptionType[] | Record<string, string | number>;

type OptionFormat = 'AUTOCOMPLETE' | 'SELECT'

export interface UseGetOptionsConditionalParams{
  optionFormat?: OptionFormat; // Indicates the expected format of the options
  includeAllOption?: boolean; // Optional flag to include the default "All" option
}

export interface UseGetOptionsParams extends UseGetOptionsConditionalParams{
  options: VariableInputOptionType
}

const DEFAULT_OPTION: AutocompleteOption = {label: "All", id: ""};

/*
 * Normalizes a heterogeneous set of option values into a list of options
 * suitable for autocomplete or select inputs.
 *
 * The hook supports input items as:
 * - simple strings (e.g., `"Option 1"`)
 * - objects with `{ id, label }`
 * - objects with `{ value, label }`
 * - or an object map of `{ [key]: value }`, which is converted to `{ id: key, label: value }`.
 *
 * It always returns options in the format specified by `optionFormat` and can
 * optionally prepend a default `"All"` option when `includeAllOption` is `true`.
 *
 * @param params - Configuration for how to normalize the provided options.
 * @returns An array of normalized options conforming to the specified `OptionFormat`.
*/

const generateOption = (optionFormat: OptionFormat = 'AUTOCOMPLETE', value: string, label: string): OptionType => {
  if (optionFormat === 'SELECT') {
    return {
      value,
      label,
    };
  } else {
    return {
      id: value,
      label,
    };
  }
}

function useGetOptions(params: UseGetOptionsParams & { optionFormat: 'SELECT' }): SelectInputProps[];
function useGetOptions(params: UseGetOptionsParams & { optionFormat?: 'AUTOCOMPLETE' }): AutocompleteOption[];
function useGetOptions(params: UseGetOptionsParams): OptionType[];
function useGetOptions({
  optionFormat = 'AUTOCOMPLETE',
  options,
  includeAllOption = true
}: UseGetOptionsParams): OptionType[] {

  const memoOptions = useMemo(() => {
    const memoizedOptions = includeAllOption ? [generateOption(optionFormat, DEFAULT_OPTION.id, DEFAULT_OPTION.label)] : [];
    const inputOptions = Array.isArray(options) ? options : Object.entries(options).map(([key, value]) => ({ id: String(key), label: String(value) }));
    memoizedOptions.push(
      ...inputOptions.map((item) => {
        /*
        * The VariableInputOptionType can have different formats for the options:
        * 1. A simple string (e.g., "Option 1")
        * 2. An object with 'id' and 'label' properties (e.g., { id: "option1", label: "Option 1" })
        * 3. An object with 'value' and 'label' properties (e.g., { value: "option1", label: "Option 1" })
        * We need to normalize these into the format requested.
        */
        if (typeof item === 'string') {
          return generateOption(optionFormat, item, item);
        } else if (typeof item === 'object' && item !== null) {
          if ('label' in item && 'id' in item) {
            return generateOption(optionFormat, String(item.id), String(item.label));
          } else if ('label' in item && 'value' in item) {
            return generateOption(optionFormat, String(item.value), String(item.label));
          }
        }

        return undefined;
      }).filter(option => option !== undefined)
    );

    return memoizedOptions;
  }, [options, includeAllOption, optionFormat]);

  return memoOptions
}

export {
  useGetOptions,
  type OptionType,
  type VariableInputOptionType,
  type SelectInputProps,    //re-export this just so consumers can get both the component and the options type from the same place
  type AutocompleteOption,  //re-export this just so consumers can get both the component and the options type from the same place
};
